import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FilterableTableHeader } from "../components/ui/filterable-table-header";
import { IPWhitelistModal } from "../components/staff/IPWhitelistModal";
import { TransferAgentModal } from "../components/staff/TransferAgentModal";
import { FastLoginModal } from "../components/staff/FastLoginModal";
import { Plus, Search, Shield, Link as LinkIcon, Edit, Trash2, ArrowRightLeft, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { deleteUser, fetchAvailableUsers, fetchUsers } from "../services/crmServices/userService";
import Swal from "sweetalert2";
import { useAuth } from "../hooks/useAuth";

export function Staff() {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [staff, setStaff] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showIPModal, setShowIPModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showFastLoginModal, setShowFastLoginModal] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [positionFilter, setPositionFilter] = useState([]);

    // Get unique positions for filter
    const uniquePositions = [...new Set(staff.map((member) => member.position.name))].sort();

    const filteredStaff = staff.filter((member) => {
        const matchesSearch =
            member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member._id.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesPosition = positionFilter.length === 0 || positionFilter.includes(member.position.name);

        return matchesSearch && matchesPosition;
    });

    const totalPages = Math.ceil(filteredStaff.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedStaff = filteredStaff.slice(startIndex, startIndex + pageSize);

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    const handleAddStaff = () => {
        navigate("/staff/create");
    };

    const handleEditStaff = (staffMember) => {
        navigate(`/staff/edit/${staffMember._id}`);
    };

    const handleTransferAgent = (agent) => {
        if (!/^admin|^manager/.test(agent.position)) {
            setSelectedAgent(agent);
            setShowTransferModal(true);
        } else {
            toast.error("Only agents can be transferred between desks");
        }
    };

    const handleTransferSuccess = () => {
        setShowTransferModal(false);
        setSelectedAgent(null);
        initPage();
    };

    const handleIPWhitelist = () => {
        setShowIPModal(true);
    };

    const handleFastLogin = (staffMember) => {
        if (staffMember.canFastLogin) {
            setSelectedStaff(staffMember);
            setShowFastLoginModal(true);
        } else {
            toast.error("Fast login is disabled for this staff member. Enable it in user settings first.");
        }
    };

    const getViewLeadsColor = (viewLeads) => {
        switch (viewLeads) {
            case "All":
                return "bg-green-100 text-green-800";
            case "Desk":
                return "bg-blue-100 text-blue-800";
            case "Floor":
                return "bg-purple-100 text-purple-800";
            case "Assigned":
                return "bg-orange-100 text-orange-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    useEffect(() => {
        if (user.position.role === "agent") {
            navigate("/");
        }

        initPage();
    }, []);

    const initPage = () => {
        setLoading(true);
        fetchAvailableUsers()
            .then((res) => {
                if (res?.data?.length) {
                    // setStaff(res.data.filter((member) => member._id !== user._id));
                    setStaff(res.data);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDelete = (_id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this staff member!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, keep it"
        }).then((result) => {
            if (result.isConfirmed) {
                deleteUser({ _id }).then((res) => {
                    if (res.status !== 200) {
                        toast.error("Something went wrong while deleting the staff member.");
                        return;
                    } else {
                        toast.success("Staff member deleted successfully");
                        initPage();
                    }
                });
            }
        });
    };

    const handleBack = () => {
        navigate("/");
    };

    const handleFilterChange = (filterKey, values) => {
        if (filterKey === "position") {
            setPositionFilter(values);
            setCurrentPage(1);
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Staff Management</h1>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                    <div className="relative w-full sm:max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search by ID, name, or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>

                    <div className="flex space-x-3 w-full sm:w-auto">
                        {user.position === "Admin" && (
                            <Button
                                onClick={handleIPWhitelist}
                                variant="outline"
                                className="flex-1 sm:flex-none border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                            >
                                <Shield className="h-4 w-4 mr-2" />
                                IP Whitelist
                            </Button>
                        )}

                        {user.permissions.staff.create && (
                            <Button
                                onClick={handleAddStaff}
                                className="flex-1 sm:flex-none"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Staff
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="rounded-md border overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Actions</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>User Name</TableHead>
                            <TableHead>Email</TableHead>
                            {user.position.role === "admin" && <TableHead>Fast Login</TableHead>}
                            <FilterableTableHeader
                                filterKey="position"
                                options={uniquePositions}
                                selectedValues={positionFilter}
                                onFilterChange={handleFilterChange}
                                placeholder="Filter positions..."
                                searchPlaceholder="Search positions..."
                            >
                                Position
                            </FilterableTableHeader>
                            <TableHead>Desk</TableHead>
                            <TableHead>Leads</TableHead>
                            <TableHead>View Leads</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedStaff.map((member) => (
                            <TableRow key={member._id}>
                                <TableCell>
                                    <div className="flex space-x-1">
                                        {user.permissions.staff.update && (
                                            <Button size="sm" variant="outline" onClick={() => handleEditStaff(member)}>
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                        )}
                                        {user.permissions.staff.delete && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleDelete(member._id)}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        )}

                                        {user.position === "Admin" && !/^admin|^manager/.test(member.position) && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleTransferAgent(member)}
                                                title="Transfer Agent"
                                            >
                                                <ArrowRightLeft className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="font-medium">
                                    {member.firstName + (member.lastName ? " " + member.lastName : "")}
                                </TableCell>
                                <TableCell className="font-medium">{member.userName || "-"}</TableCell>
                                <TableCell className="max-w-48 truncate">{member.email}</TableCell>
                                {user.position.role === "admin" && (
                                    <TableCell>
                                        {member.canFastLogin ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleFastLogin(member)}
                                                className="text-indigo-600 hover:text-indigo-700 border-indigo-300 hover:border-indigo-400"
                                            >
                                                <LinkIcon className="h-4 w-4" />
                                            </Button>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </TableCell>
                                )}

                                <TableCell>
                                    <Badge variant="outline">{member.position.name}</Badge>
                                </TableCell>
                                <TableCell>
                                    {member.position.role === "admin" && "-"}
                                    {member.position.role === "floorManager" && (
                                        <div className="flex flex-wrap gap-1">
                                            {member.floorDesks?.map((desk, index) => (
                                                <Badge key={index} variant="secondary" className="text-xs">
                                                    {desk.name}
                                                </Badge>
                                            )) || "-"}
                                        </div>
                                    )}
                                    {member.position.role === "deskManager" && (
                                        <div className="flex flex-wrap gap-1">
                                            {member.desks?.map((desk, index) => (
                                                <Badge key={index} variant="secondary" className="text-xs">
                                                    {desk.name}
                                                </Badge>
                                            )) || "-"}
                                        </div>
                                    )}

                                    {member.position.role === "agent" && member.agentDesk && (
                                        <Badge variant="secondary" className="text-xs">
                                            {member.agentDesk.name}
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {member.position.role !== "agent" ? (
                                        <Badge variant="secondary" className="bg-gray-100">
                                            -
                                        </Badge>
                                    ) : (
                                        <Badge variant="secondary" className="text-xs">
                                            {member.leadCount || 0}
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant="secondary"
                                        className={getViewLeadsColor(member.viewLeadsScope ?? "Assigned")}
                                    >
                                        {member.viewLeadsScope ?? "Assigned"}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentPage === 1}>
                        Previous
                    </Button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <Button variant="outline" size="sm" onClick={handleNext} disabled={currentPage === totalPages}>
                        Next
                    </Button>
                </div>
            </div>

            {filteredStaff.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-gray-400 text-lg mb-2">😔</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No staff members found</h3>
                    <p className="text-gray-500">Try adjusting your search criteria</p>
                </div>
            )}

            <IPWhitelistModal open={showIPModal} onClose={() => setShowIPModal(false)} />

            <TransferAgentModal
                open={showTransferModal}
                onClose={() => setShowTransferModal(false)}
                onSuccess={handleTransferSuccess}
                agent={selectedAgent}
            />

            <FastLoginModal
                open={showFastLoginModal}
                onClose={() => {
                    setShowFastLoginModal(false);
                    setSelectedStaff(null);
                }}
                staff={selectedStaff}
            />
        </div>
    );
}
